import { useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  type ExpandState,
  isPathExpanded,
  kindOf,
} from './jsonTreeNavigation';
import { JsonTreeEditableLeaf } from './JsonTreeEditableLeaf';
import { jsonTreeChildPath, type JsonValue } from './jsonTreeEdit';
import { AddChildAffordance, RemoveButton } from './JsonTreeNodeAffordances';
import { renderPrimitive, summarize } from './JsonTreePrimitives';

export type TreeEditApi = {
  setLeaf: (path: string, value: JsonValue) => void;
  removeKey: (path: string) => void;
  removeElement: (path: string) => void;
  addObjectKey: (parentPath: string, key: string) => void;
  addArrayElement: (parentPath: string) => void;
};

export type TreeApi = {
  expandState: ExpandState;
  setExpanded: (path: string, expanded: boolean) => void;
  focusedPath: string | null;
  setFocusedPath: (path: string) => void;
  registerRef: (path: string, el: HTMLDivElement | null) => void;
  edit?: TreeEditApi;
};

export type JsonTreeNodeProps = {
  label: string | number | null;
  value: unknown;
  depth: number;
  path: string;
  api: TreeApi;
};

export function JsonTreeNode({ label, value, depth, path, api }: JsonTreeNodeProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { registerRef, edit } = api;
  useEffect(() => {
    registerRef(path, ref.current);
    return () => registerRef(path, null);
  }, [path, registerRef]);

  const kind = kindOf(value);
  const isContainer = kind === 'object' || kind === 'array';
  const expanded = isContainer && isPathExpanded(api.expandState, path, depth);
  const isFocused = api.focusedPath === path;
  const tabIndex = isFocused ? 0 : -1;
  const isRoot = path === 'root';

  const labelEl = label !== null ? (
    <span translate="no" className="text-muted-foreground">
      {typeof label === 'number' ? label : `"${label}"`}
      <span className="text-muted-foreground/60">: </span>
    </span>
  ) : null;

  if (!isContainer) {
    return (
      <div
        ref={ref}
        role="treeitem"
        tabIndex={tabIndex}
        className="flex items-baseline gap-1 leading-5 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
        data-testid={`json-tree-node-${path}`}
        onFocus={(e) => {
          if (e.target === e.currentTarget) api.setFocusedPath(path);
        }}
      >
        <span className="inline-block w-4 flex-shrink-0" />
        {labelEl}
        {edit ? (
          <JsonTreeEditableLeaf
            path={path}
            value={value}
            kind={kind}
            onCommit={(next) => edit.setLeaf(path, next)}
          />
        ) : (
          renderPrimitive(value, kind)
        )}
        {edit && !isRoot && (
          <RemoveButton
            path={path}
            onClick={() =>
              typeof label === 'number'
                ? edit.removeElement(path)
                : edit.removeKey(path)
            }
          />
        )}
      </div>
    );
  }

  const entries: Array<[string | number, unknown]> = kind === 'array'
    ? (value as unknown[]).map((v, i) => [i, v])
    : Object.entries(value as Record<string, unknown>);

  const isEmpty = entries.length === 0;
  const open = kind === 'array' ? '[' : '{';
  const close = kind === 'array' ? ']' : '}';
  const toggle = () => api.setExpanded(path, !expanded);

  return (
    <div
      ref={ref}
      role="treeitem"
      tabIndex={tabIndex}
      aria-expanded={isEmpty ? undefined : expanded}
      data-testid={`json-tree-node-${path}`}
      className="focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
      onFocus={(e) => {
        if (e.target === e.currentTarget) api.setFocusedPath(path);
      }}
    >
      <div className="flex items-baseline gap-1 leading-5">
        {isEmpty ? (
          <span className="inline-block w-4 flex-shrink-0" aria-hidden="true" />
        ) : (
          <button
            type="button"
            className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            onClick={toggle}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            data-testid={`json-tree-toggle-${path}`}
            tabIndex={-1}
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            )}
          </button>
        )}
        {labelEl}
        {isEmpty ? (
          <span>
            {open}
            {close}
          </span>
        ) : !expanded ? (
          <>
            <span>{open}</span>
            <span className="text-muted-foreground/70 text-xs mx-1">
              {summarize(value, kind)}
            </span>
            <span>{close}</span>
          </>
        ) : (
          <span>{open}</span>
        )}
        {edit && !isRoot && (
          <RemoveButton
            path={path}
            onClick={() =>
              typeof label === 'number'
                ? edit.removeElement(path)
                : edit.removeKey(path)
            }
          />
        )}
      </div>
      {expanded && !isEmpty && (
        <div role="group" className="ml-4 border-l border-border pl-2">
          {entries.map(([k, v]) => (
            <JsonTreeNode
              key={String(k)}
              label={k}
              value={v}
              depth={depth + 1}
              path={jsonTreeChildPath(path, k)}
              api={api}
            />
          ))}
          {edit && (
            <AddChildAffordance parentPath={path} containerKind={kind} edit={edit} />
          )}
        </div>
      )}
      {expanded && isEmpty && edit && (
        <div className="ml-4 border-l border-border pl-2">
          <AddChildAffordance parentPath={path} containerKind={kind} edit={edit} />
        </div>
      )}
      {expanded && !isEmpty && <div className="leading-5">{close}</div>}
    </div>
  );
}
